<template>
  <div v-if="isVisible" class="popup-overlay" @click.self="handleOverlayClick">
    <div class="popup-container" ref="popupRef">
      <div v-if="currentMessage" class="dialog-content">
        <div v-if="currentMessage.disposable" @click="close" class="close-button">✖</div>
        <img v-if="currentMessage.image" :src="currentMessage.image" class="message-image" />
        <div class="subject" v-html="currentMessage.subject"></div>
        <div class="content" v-html="currentMessage.content" @click="clickedContent"></div>
        <button class="btn" v-if="!currentMessage.showForEver" @click="markAsReadAndClose">
          {{ $t('common.close') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onUnmounted, watch, ref, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { readMessage } from '@/api/backoffice';

const POLLING_INTERVAL = 300_000;
const MESSAGE_DELAY = 500;
const UNACCEPTABLE_ROUTES = ['/ProfilesGate', '/'];

const store = useStore();
const route = useRoute();

const isVisible = ref(false);
const pollingInterval = ref(null);
const hasJustLoggedIn = ref(false);

const seenMessages = ref([]);

const isLoggedIn = computed(() => store.state.auth?.isLoggedIn);
const guidCredentials = computed(() => store.state.auth?.guidCredentials || null);
const messages = computed(() => store.state.leadMessages?.leadMessage || store.state.leadMessages?.initialMessage || null);
const isAcceptableRoute = computed(() => !UNACCEPTABLE_ROUTES.includes(route.path));

const currentMessage = computed(() => {
  const allMessages = Array.isArray(messages.value) ? messages.value : [];
  const unseenMessage = allMessages.find((msg) => !seenMessages.value.includes(msg.messageId));
  return unseenMessage || null;
});

const toggleBodyOverflow = (overflow) => {
  document.body.style.overflow = overflow;
  document.documentElement.style.overflow = overflow;
};

const loadSeenMessages = () => {
  try {
    const stored = localStorage.getItem('seenMessages');
    seenMessages.value = stored ? JSON.parse(stored) : [];
  } catch (e) {
    console.error('Error loading seenMessages:', e);
    seenMessages.value = [];
  }
};

const saveSeenMessages = () => {
  try {
    localStorage.setItem('seenMessages', JSON.stringify(seenMessages.value));
  } catch (e) {
    console.error('Error saving seenMessages:', e);
  }
};

const addSeenMessage = (messageId) => {
  if (messageId && !seenMessages.value.includes(messageId)) {
    seenMessages.value.push(messageId);
    saveSeenMessages();
  }
};

const openModal = () => {
  if (!isVisible.value && currentMessage.value && isAcceptableRoute.value && hasJustLoggedIn.value) {
    isVisible.value = true;
    toggleBodyOverflow('hidden');
    document.addEventListener('keydown', handleEscClose);
  }
};

const closeModal = () => {
  if (isVisible.value) {
    isVisible.value = false;
    toggleBodyOverflow('');
    document.removeEventListener('keydown', handleEscClose);
  }
};

const close = async () => {
  closeModal();

  await nextTick();
  await nextTick();

  setTimeout(() => {
    if (currentMessage.value && isAcceptableRoute.value && hasJustLoggedIn.value && !isVisible.value) {
      openModal();
    }
  }, MESSAGE_DELAY);
};

const initFetch = () => {
  const creds = guidCredentials.value;
  if (!creds) return;

  const leadExists = typeof creds.lead === 'string' && creds.lead.length > 0;
  const guidExists = typeof creds.guid === 'string' && creds.guid.length > 0;

  if (guidExists && leadExists) {
    store.dispatch('leadMessages/fetchLead', {
      guid: creds.guid,
      lead: creds.lead,
    });
  } else if (guidExists && !leadExists) {
    store.dispatch('leadMessages/fetchInitial', {
      guid: creds.guid,
      shouldFetch: true,
    });
  }
};

const startPolling = () => {
  if (pollingInterval.value) return;
  pollingInterval.value = setInterval(initFetch, POLLING_INTERVAL);
  initFetch();
};

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
};

const checkAndShowPopup = () => {
  if (isAcceptableRoute.value && currentMessage.value && hasJustLoggedIn.value && !isVisible.value) {
    openModal();
  }
};

const markAsReadAndClose = async () => {
  const messageId = currentMessage.value?.messageId;

  if (messageId) {
    const token = store.state.auth?.currentUser?.token;
    if (token) {
      try {
        await readMessage(token, messageId);
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }

    addSeenMessage(messageId);
  }

  await close();
};

const clickedContent = (e) => {
  if (e.target?.hasAttribute('data-close-popup')) close();
};

const handleOverlayClick = (e) => {
  if (e.target === e.currentTarget && currentMessage.value?.disposable) {
    close();
  }
};

const handleEscClose = (event) => {
  if (event.key === 'Escape') close();
};

watch(
  isLoggedIn,
  (newVal, oldVal) => {
    if (!oldVal && newVal) {
      hasJustLoggedIn.value = true;
      checkAndShowPopup();
    }
  },
  { immediate: true }
);

watch(() => route.path, checkAndShowPopup, { immediate: true });

watch(
  guidCredentials,
  (newVal) => {
    if (newVal) {
      startPolling();
    } else {
      stopPolling();
    }
  },
  { immediate: true }
);

watch(
  currentMessage,
  (newMessage) => {
    if (newMessage && isAcceptableRoute.value && hasJustLoggedIn.value && !isVisible.value) {
      nextTick(() => checkAndShowPopup());
    }
  },
  { immediate: true }
);

watch(seenMessages, saveSeenMessages, { deep: true });

watch(
  isLoggedIn,
  (newVal, oldVal) => {
    if (!oldVal && newVal) {
      hasJustLoggedIn.value = true;
      loadSeenMessages();
      checkAndShowPopup();
    } else if (oldVal && !newVal) {
      hasJustLoggedIn.value = false;
      seenMessages.value = [];
      localStorage.removeItem('seenMessages');
      stopPolling();
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  stopPolling();
  toggleBodyOverflow('');
  if (document.removeEventListener) {
    document.removeEventListener('keydown', handleEscClose);
  }
});

loadSeenMessages();
</script>

<style scoped>
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: fadeIn 0.3s ease-out;
}

.popup-container {
  position: relative;
  min-width: 400px;
  max-width: 90vw;
  max-height: 90vh;
  padding: 30px;
  border-radius: 5px;
  background-color: rgb(26, 24, 24);
  color: white;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  overflow-y: auto;
  animation: slideIn 0.3s ease-out;
}

.dialog-content {
  padding: 10px;
  position: relative;
}

.close-button {
  position: absolute;
  right: 10px;
  top: 10px;
  cursor: pointer;
  z-index: 1;
  font-size: 20px;
  color: white;
  transition: color 0.2s ease;
}

.close-button:hover {
  color: #dc3545;
}

.message-image {
  max-width: 100%;
  height: auto;
  margin-bottom: 15px;
  border-radius: 4px;
}

.subject {
  font-size: 1.2em;
  font-weight: bold;
  margin-bottom: 15px;
  line-height: 1.4;
}

.content {
  margin-bottom: 20px;
  line-height: 1.5;
}

.btn {
  margin-top: 10px;
  padding: 8px 16px;
  color: #fff;
  background-color: #dc3545;
  border: 1px solid #dc3545;
  border-radius: 0.25rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  outline: none;
  width: 100%;
}

.btn:hover {
  background-color: #c82333;
  border-color: #bd2130;
}

.btn:focus {
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.25);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@media (max-width: 768px) {
  .popup-container {
    min-width: 300px;
    padding: 20px;
    margin: 10px;
  }
  .popup-overlay {
    padding: 10px;
  }
}
</style>
